// src/pages/ArchiveList.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import BBSHeader from '../components/BBS/BBSHeader';
import BBSNav from '../components/BBS/BBSNav';
import StatusBar from '../components/BBS/StatusBar';
// CHANGED: import setPageMeta and new setCollectionSchema
import { setPageMeta, setCollectionSchema } from '../utils/seo';

const TRANSCRIPTS_PER_PAGE = 5;

function weightedShuffle(transcripts) {
  if (!transcripts.length) return [];

  const now = Date.now();
  const weighted = transcripts.map(t => {
    const age =
      now - new Date(t.publishedAt || t.createdAt).getTime();
    const dayAge = age / (1000 * 60 * 60 * 24);
    const weight = Math.max(1, 100 / (1 + dayAge * 0.1));
    return { transcript: t, weight, rand: Math.random() * weight };
  });

  weighted.sort((a, b) => b.rand - a.rand);
  return weighted.map(w => w.transcript);
}

export default function ArchiveList() {
  const [allTranscripts, setAllTranscripts] = useState([]);
  const [shuffled, setShuffled] = useState([]);
  const [visibleCount, setVisibleCount] = useState(TRANSCRIPTS_PER_PAGE);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const navigate = useNavigate();

  // CHANGED: set page-level SEO on mount, clean up on unmount
  useEffect(() => {
    setPageMeta({
      title: 'Archived Transcripts',
      description:
        'Browse all archived transmissions and transcripts ' +
        'from the OPHIDIA CLOUD.',
      url: 'https://ophidia.cloud/archives',
    });

    return () => {
      // Reset to defaults when navigating away
      setPageMeta();
      setCollectionSchema(null);
    };
  }, []);

  useEffect(() => {
    fetch('/api/stories')
      .then(res => res.json())
      .then(data => {
        setAllTranscripts(data);
        setShuffled(weightedShuffle(data));
        setLoading(false);
        // CHANGED: inject CollectionPage JSON-LD once stories load
        setCollectionSchema(data);
      })
      .catch(err => {
        console.error('Failed to load archived transcripts:', err);
        setLoading(false);
      });
  }, []);

  const reshuffle = useCallback(() => {
    setShuffled(weightedShuffle(allTranscripts));
    setVisibleCount(TRANSCRIPTS_PER_PAGE);
  }, [allTranscripts]);

  const isFiltering = filter.trim().length > 0;

  const filtered = isFiltering
    ? allTranscripts.filter(
        t =>
          t.title.toLowerCase().includes(filter.toLowerCase()) ||
          (t.tags &&
            t.tags.some(tag =>
              tag.toLowerCase().includes(filter.toLowerCase())
            ))
      )
    : shuffled;

  const visible = isFiltering
    ? filtered
    : filtered.slice(0, visibleCount);
  const hasMore = !isFiltering && visibleCount < filtered.length;

  function formatDate(dateStr) {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
    });
  }

  function loadMore() {
    setVisibleCount(prev => prev + TRANSCRIPTS_PER_PAGE);
  }

  return (
    <div>
      <BBSHeader title="ARCHIVED TRANSCRIPTS" />
      <BBSNav />

      {/* CHANGED: semantic <h1> — visually hidden but crawlers see it */}
      <h1 className="sr-only">
        Archived Transcripts — The God Circuit
      </h1>

      <div style={{ marginBottom: '15px' }}>
        <div
          style={{
            color: '#555',
            marginBottom: '8px',
            fontSize: '0.9rem',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '8px',
          }}
        >
          <span>
            {allTranscripts.length} transcripts in archive
            {!isFiltering &&
              ` — displaying ${Math.min(
                visibleCount,
                shuffled.length
              )} of ${shuffled.length}`}
          </span>
          {!isFiltering && (
            <button
              onClick={reshuffle}
              className="bbs-button"
              style={{ padding: '4px 12px', fontSize: '0.8rem' }}
            >
              ↻ RESHUFFLE
            </button>
          )}
        </div>
        {/* CHANGED: added aria-label for accessibility */}
        <input
          type="text"
          value={filter}
          onChange={e => setFilter(e.target.value)}
          placeholder="Search transcripts or tags..."
          aria-label="Search transcripts or tags"
          className="admin-input"
          style={{ maxWidth: '400px' }}
        />
      </div>

      <hr className="bbs-divider" />

      {loading ? (
        <div className="bbs-loading">
          RETRIEVING ARCHIVED TRANSCRIPTS
          <span className="bbs-loading-dots" />
        </div>
      ) : visible.length === 0 ? (
        <div
          style={{
            color: '#555',
            padding: '20px',
            textAlign: 'center',
          }}
        >
          NO TRANSCRIPTS FOUND
        </div>
      ) : (
        <>
          {visible.map((transcript, index) => (
            // CHANGED: <article> with <h2> for semantic structure
            <article
              key={transcript.id}
              className="bbs-story-card"
              onClick={() =>
                navigate(`/archives/${transcript.slug}`)
              }
              style={{ cursor: 'pointer' }}
            >
              <h2
                className="story-title"
                style={{ margin: 0, fontSize: 'inherit' }}
              >
                <span
                  style={{
                    color: '#00ff41',
                    marginRight: '10px',
                  }}
                >
                  [{String(index + 1).padStart(2, '0')}]
                </span>
                {transcript.title}
              </h2>
              <div className="story-date">
                {/* CHANGED: <time> element with machine-readable datetime */}
                <time
                  dateTime={
                    transcript.publishedAt || transcript.createdAt
                  }
                >
                  {formatDate(
                    transcript.publishedAt || transcript.createdAt
                  )}
                </time>
                {transcript.substackUrl && (
                  <span
                    style={{
                      marginLeft: '15px',
                      color: '#ffb000',
                    }}
                  >
                    [ALSO ON SUBSTACK]
                  </span>
                )}
              </div>
              {/* CHANGED: <p> instead of <div> for excerpt */}
              <p
                className="story-excerpt"
                style={{ margin: '4px 0' }}
              >
                {transcript.excerpt}
              </p>
              <div className="story-tags">
                {transcript.tags &&
                  transcript.tags.map(tag => (
                    <span key={tag} className="story-tag">
                      {tag}
                    </span>
                  ))}
              </div>
            </article>
          ))}

          {hasMore && (
            <div
              style={{ textAlign: 'center', padding: '20px 0' }}
            >
              <button
                onClick={loadMore}
                className="bbs-button"
                style={{ padding: '10px 30px' }}
              >
                ▼ LOAD MORE TRANSCRIPTS (
                {filtered.length - visibleCount} remaining)
              </button>
            </div>
          )}

          {!hasMore && visible.length > 0 && !isFiltering && (
            <div
              style={{
                textAlign: 'center',
                padding: '20px 0',
                color: '#333',
                fontSize: '0.85rem',
              }}
            >
              ═══ END OF ARCHIVE — {allTranscripts.length}{' '}
              TRANSCRIPTS CATALOGUED ═══
            </div>
          )}
        </>
      )}

      <StatusBar />
    </div>
  );
}